import { Component, Input, ElementRef, Renderer2, OnChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as _intersection from 'lodash/intersection';

import { MenuService } from '@core/services/menu/menu.service';
import { CartService } from '@core/services/cart/cart.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { PriceService } from '@core/services/price/price.service';
import { PassService } from '@core/services/pass/pass.service';
import { StorageService } from '@core/services/util/storage.service';
import { LocalStorageService } from '@core/services/util/local-storage.service';

import { MenuModel } from '@shared/models/menu-model';

import { BaseComponent } from '@core/components/base/base.component';

import { environment } from '@environment';
import { MenuUtilService } from '@core/services/util/menu-util.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { PonchoUIModule } from '@poncho/ui';
import { DeferImageLoadDirective } from '../../directives/defer-image-load.directive';
import { NgClass } from '@angular/common';
import { BottomStripComponent } from '../bottom-strip/bottom-strip.component';
import { PassStripComponent } from '../pass-strip/pass-strip.component';
import { cartStrip } from '@app/shared/models/bottom-strip';
import { CatalogApiResponse, Outlet } from '@app/shared/models/api-response/catalog-api-response';
import { TruncateTextComponent } from '../truncate-text/truncate-text.component';

@Component({
    selector: 'app-product-list',
    templateUrl: './product-list.component.html',
    styleUrls: ['./product-list.component.scss'],
    standalone: true,
    imports: [DeferImageLoadDirective, PonchoUIModule, NgClass, BottomStripComponent, PassStripComponent, TruncateTextComponent]
})
export class ProductListComponent extends BaseComponent implements OnChanges {
    filteredMenu = [];
    menuModel: MenuModel;
    saleableProducts = [];

    destroySubject$: Subject<void> = new Subject();

    @Input() queryText: any;
    @Input() searchView: boolean;
    showSearchView = true;

    _element: ElementRef;
    dropdownDOM: any;

    showRecentSearch = true;
    recentSearch = [];
    noResultFound = false;
    brandId: any;

    product_prices: any = {};
    passModel: any = {};
    productQuantities = {};
    cartStrip = cartStrip;
    showCartStrip: boolean;
    outlet: Outlet;

    constructor(
        private outletService: OutletService,
        private menuService: MenuService,
        private cartService: CartService,
        private priceService: PriceService,
        private passService: PassService,
        private elRef: ElementRef,
        private renderer: Renderer2,
        private storageService: StorageService,
        private localStorage: LocalStorageService,
        private menuUtil: MenuUtilService,
        private gtmService: GtmService
    ) {
        super();
    }

    ngOnInitBrowser() {
        this._element = this.elRef;

        this.brandId = this.storageService.get('brand_id') || environment.brand.id;

        this.menuService.menuModel.pipe(takeUntil(this.destroySubject$)).subscribe((data: MenuModel) => {
            if (data && data.menu) {
                this.menuModel = data;
                const saleableCategories =
                    this.menuModel.menu.sequence.map(category => {
                        return category.saleable && !category.is_subscribable && category.id;
                    }) || [];

                this.saleableProducts = this.menuModel.menu.data.filter(data => {
                    return saleableCategories.includes(data.category_id) && data.label;
                });
            }
        });

        this.outletService.state.pipe(takeUntil(this.destroySubject$)).subscribe((data: CatalogApiResponse) => {
            if (data && data.outlet) {
                this.outlet = data.outlet;
                const recentSearchList = this.localStorage.get('recent_search') || Object.create(null);
                this.recentSearch = recentSearchList[this.brandId] || [];
            }
        });

        this.cartService.state.pipe(takeUntil(this.destroySubject$)).subscribe((cart: any) => {
            const cart_items = Object.values(cart.items);
            this.productQuantities = this.cartService.getProductQuantities(cart_items);
            this.showCartStrip = cart_items.length > 0;
        });
        

        this.passService.initCustomerPasses();
        this.priceService
            .getMenuPrices()
            .pipe(takeUntil(this.destroySubject$))
            .subscribe(prices => {
                if (prices?.products) {
                    this.product_prices = prices.products;
                }
            });
        this.passService.passModel$.pipe(takeUntil(this.destroySubject$)).subscribe((data: any) => (this.passModel = data));
    }

    ngOnChanges() {
        this.showData(this.queryText);
        this.toggleView(this.searchView);
    }

    ngOnDestroyBrowser() {
        this.destroySubject$.next();
        this.destroySubject$.complete();
    }

    showData(inputValue: string) {
        if (inputValue && inputValue.length > 1) {
            this.gtmService.logWebAnalyticsEvent('search_page', 'Queried for:' + inputValue);
            this.showRecentSearch = false;
            const search = this.productStringMatching(inputValue);

            if (search && search.length) {
                this._hideDropdown();
                this.noResultFound = false;
                this.createSearchedProductList(search);
            } else {
                this.noResultFound = true;
                this._hideDropdown();
            }
        } else {
            this.showRecentSearch = true;
            this._hideDropdown();
        }
    }

    productStringMatching(queryText: string) {
        const productLabel = this.saleableProducts.map((data: any) => {
            return data.label;
        });

        const strArr = queryText.toLowerCase().split(' ');

        return strArr.reduce((acc, data) => {
            return acc.filter(label => {
                return label.toLowerCase().includes(data);
            });
        }, productLabel);
    }

    createSearchedProductList(search: Array<any>) {
        const parent = this._element.nativeElement;
        this.dropdownDOM = this.renderer.createElement('div');
        this.renderer.addClass(this.dropdownDOM, 'search-result');
        const ulElement = this.renderer.createElement('ul');

        while (ulElement.hasChildNodes()) {
            ulElement.removeChild(ulElement.lastChild);
        }

        const li = this.renderer.createElement('li');
        const productName = this.renderer.createText(
            `View all results for '${this.queryText.charAt(0).toUpperCase() + this.queryText.slice(1)}'`
        );
        this.renderer.appendChild(li, productName);

        this.renderer.listen(li, 'click', () => {
            this._onSelect(this.queryText);
        });

        this.renderer.appendChild(ulElement, li);

        for (let i = 0; i < search.length; i++) {
            // create the DOM element
            const li = this.renderer.createElement('li');
            const productName = this.renderer.createText(search[i]);
            this.renderer.appendChild(li, productName);

            this.renderer.listen(li, 'click', () => {
                this._onSelect(search[i]);
            });

            this.renderer.appendChild(ulElement, li);
        }
        this.renderer.appendChild(this.dropdownDOM, ulElement);
        this.renderer.appendChild(parent, this.dropdownDOM);
    }

    toggleView(value: boolean) {
        this.showSearchView = value;
    }

    cachedResult = {};
    _onSelect(productName: any) {
        productName = productName.toLowerCase();
        (document.getElementById('searchbox') as HTMLInputElement).value = productName;
        this.gtmService.logWebAnalyticsEvent('search_page', 'Selected: ' + productName);

        if (productName in this.cachedResult) {
            this.filteredMenu = this.cachedResult[productName];
            this.gtmService.logWebAnalyticsEvent('search_page', 'Repeated-Search: ' + productName);
        } else {
            const result = this.productStringMatching(productName);
            this.filteredMenu = [...this.saleableProducts].filter(data => {
                return result.includes(data.label);
            });
            this.cachedResult[productName] = this.filteredMenu;
        }

        this.toggleView(false);
        this.noResultFound = false;
        this.createRecentSearchList(productName);
        this._hideDropdown();
    }

    private _hideDropdown() {
        if (this.dropdownDOM) {
            this.renderer.setStyle(this.dropdownDOM, 'display', 'none');
        }
    }

    createRecentSearchList(productName) {
        if (this.recentSearch.indexOf(productName) == -1) {
            this.recentSearch = [productName, ...this.recentSearch.slice(0, 4)];

            const recentSearchList = this.localStorage.get('recent_search') || Object.create(null);
            recentSearchList[this.brandId] = this.recentSearch;
            this.localStorage.set('recent_search', recentSearchList);
        }
        this.showRecentSearch = false;
    }

    isAddButtonActive(productId) {
        return this.menuService.isAddButtonActive(productId);
    }

    getButtonLabel(productId) {
        return this.menuService.getButtonLabel(productId);
    }

    handleDisabledState(product) {
        this.menuUtil.handleDisabledState(product, this.outlet);
    }

    updateCart(operation, productId) {
        this.menuUtil.updateCart(operation, productId, this.menuModel);
    }
}
